/**
 * Warning Suppressions
 * 
 * This file contains utilities to suppress known warnings that are not critical
 * for the app functionality but may clutter the console during development.
 */

import { Platform } from 'react-native';

// Store original console methods
const originalWarn = console.warn;
const originalError = console.error;

// Known warnings to suppress
const suppressWarnings = [
  // React Navigation deprecation warnings (these are internal to React Navigation)
  '"shadow*" style props are deprecated. Use "boxShadow"',
  'props.pointerEvents is deprecated. Use style.pointerEvents',
  
  // React DevTools message (informational only)
  'Download the React DevTools for a better development experience',
  
  // React Native Web warnings for web environment
  'TouchableMixin is deprecated. Please use Pressable',
  'Unknown event handler property',
  'onStartShouldSetResponder',
  'onResponderTerminationRequest',
  'onResponderGrant',
  'onResponderMove',
  'onResponderRelease',
  'onResponderTerminate',
  
  // SQLite warnings for web environment (expected behavior)
  'SQLite not available (web environment)',
  'SQLite not available for',
  'skipping sync queue operation',
  'returning empty',
];

/**
 * Initialize warning suppressions for web environment
 */
export const initializeWarningSuppressions = () => {
  // Only suppress warnings in web environment to reduce console noise
  if (Platform.OS === 'web') {
    // Override console.warn to filter out known warnings
    console.warn = (...args: any[]) => {
      const message = args.join(' ');
      
      // Check if this warning should be suppressed
      const shouldSuppress = suppressWarnings.some(warning => 
        message.toLowerCase().includes(warning.toLowerCase())
      );
      
      // Only show warning if it's not in our suppression list
      if (!shouldSuppress) {
        originalWarn.apply(console, args);
      }
    };

    // Override console.error to filter out non-critical React Native Web errors
    console.error = (...args: any[]) => {
      const message = args.join(' ');
      
      // Don't suppress actual errors, just filter specific non-critical ones
      const shouldSuppress = suppressWarnings.some(warning => 
        message.toLowerCase().includes(warning.toLowerCase()) && !message.includes('Error:')
      );
      
      if (!shouldSuppress) {
        originalError.apply(console, args);
      }
    };

    console.log('🔇 Warning suppressions initialized for web environment');
  }
};

/**
 * Reset console methods to original state
 */
export const resetWarningSuppressions = () => {
  console.warn = originalWarn;
  console.error = originalError;
  console.log('🔊 Warning suppressions reset');
};

/**
 * Utility to log suppressed warnings in development if needed
 */
export const logSuppressedWarnings = (enable: boolean = false) => {
  if (!enable || Platform.OS !== 'web') return;

  const suppressedWarnings: string[] = [];

  console.warn = (...args: any[]) => {
    const message = args.join(' ');
    
    const shouldSuppress = suppressWarnings.some(warning => 
      message.includes(warning)
    );
    
    if (shouldSuppress) {
      suppressedWarnings.push(message);
      console.log(`🔇 Suppressed warning: ${message}`);
    } else {
      originalWarn.apply(console, args);
    }
  };

  // Log summary of suppressed warnings periodically
  setTimeout(() => {
    if (suppressedWarnings.length > 0) {
      console.group('🔇 Suppressed Warnings Summary');
      console.log(`Total suppressed: ${suppressedWarnings.length}`);
      suppressedWarnings.forEach((warning, index) => {
        console.log(`${index + 1}. ${warning}`);
      });
      console.groupEnd();
    }
  }, 5000);
};